import { useEffect, useRef, useState } from 'react';
import type { Action } from '../../game/input/input';
import { useGameInstance } from './GameContext';

/** On-screen joystick and action buttons for touch devices. */
export function TouchControls() {
  const game = useGameInstance();
  const [enabled] = useState(() => window.matchMedia?.('(pointer: coarse)').matches ?? false);
  const pad = useRef<HTMLDivElement>(null);
  const knob = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled || !game || !pad.current) return;
    const el = pad.current;
    let active: number | null = null;
    const update = (e: PointerEvent): void => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      let dx = (e.clientX - cx) / (r.width / 2);
      let dy = (e.clientY - cy) / (r.height / 2);
      const l = Math.hypot(dx, dy);
      if (l > 1) {
        dx /= l;
        dy /= l;
      }
      game.input.touchMove = { x: dx, y: -dy };
      if (knob.current) knob.current.style.transform = `translate(${dx * 38}px, ${dy * 38}px)`;
    };
    const down = (e: PointerEvent): void => {
      active = e.pointerId;
      el.setPointerCapture(e.pointerId);
      update(e);
    };
    const move = (e: PointerEvent): void => {
      if (e.pointerId === active) update(e);
    };
    const up = (e: PointerEvent): void => {
      if (e.pointerId !== active) return;
      active = null;
      game.input.touchMove = { x: 0, y: 0 };
      if (knob.current) knob.current.style.transform = '';
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
    };
  }, [enabled, game]);

  if (!enabled || !game) return null;
  const btn = (label: string, a: Action) => (
    <button
      className="touch-btn"
      onPointerDown={(e) => {
        e.preventDefault();
        game.triggerAction(a);
      }}
    >
      {label}
    </button>
  );
  return (
    <>
      <div className="touch-joystick" ref={pad} aria-label="Movement joystick">
        <div className="touch-knob" ref={knob} />
      </div>
      <div className="touch-buttons">
        {btn('Talk', 'interact')}
        {btn('Target', 'target_next')}
        {btn('Dodge', 'dodge')}
        {btn('Skill 2', 'skill1')}
        {btn('Skill 3', 'skill2')}
        {btn('Skill 4', 'skill3')}
        {btn('Attack', 'skill0')}
        {btn('Bag', 'inventory')}
        {btn('Quests', 'quests')}
      </div>
    </>
  );
}
